import React from "react";
import { ChatHeader } from "./ChatHeader";
import { ChatInputArea } from "./ChatInputArea";
import { ChatMessageList } from "./ChatMessageList";
import { SidebarNavigation } from "./SidebarNavigation";
import type { ActiveSidebar } from "../../types/chat-sidebar-types";
import type { Conversation } from "../../hooks/useChat";
import type { ReasoningEffort } from "@flowstate/types";
import type { ContextWindowUsage } from "../../hooks/useContextWindowUsage";
import type { ExtendedMessage, MessageAttachment } from "../../types/chat";

export interface ChatMainColumnProps {
  isFullscreen: boolean;
  onClose: () => void;
  onToggleFullscreen: () => void;
  activeSidebar: ActiveSidebar;
  setActiveSidebar: (sidebar: ActiveSidebar) => void;
  currentConversation: Conversation | null;
  startNewConversation: () => void;
  isMobile: boolean;
  messages: ExtendedMessage[];
  isError: boolean;
  handleReplayMessage: (messageId: string) => void;
  onFollowUpClick: (text: string) => void;
  isInitializing: boolean;
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  handleSendMessage: (text?: string) => Promise<void>;
  isLoading: boolean;
  currentConversationId: string | undefined;
  handleFileUploaded: (attachment: MessageAttachment) => void;
  pendingAttachments: MessageAttachment[];
  autoExtractTasks: boolean;
  onToggleAutoExtract: () => void;
  selectedSpecialist?: string;
  onSpecialistChange?: (specialist: string | undefined) => void;
  selectedProvider: string;
  onProviderChange: (provider: string) => void;
  selectedModel: string;
  onModelChange: (model: string) => void;
  reasoningEffort?: ReasoningEffort;
  onReasoningEffortChange?: (effort: ReasoningEffort) => void;
  isGeminiOAuthConnected: boolean;
  backgroundMode: boolean;
  onToggleBackgroundMode: () => void;
  selectedNotebookId?: string;
  onSelectNotebook?: (notebookId: string | undefined) => void;
  contextUsage: ContextWindowUsage;
  pendingQuestion?: {
    runId: string;
    question: string;
    options?: string[];
    allowFreeText?: boolean;
  } | null;
  onAnswerQuestion?: (opts: { approved?: boolean; value?: string }) => void;
  allActionItems: unknown[];
  handleActionItemsUpdate: (items: unknown[]) => void;
  executeQuickAction: (action: string) => void;
  conversationList: Conversation[];
  loadConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
  selectedContextIds: string[];
  toggleContextSelection: (id: string) => void;
  summarizeSelectedConversations: () => void;
  isSummarizing: boolean;
  summarizedContext: string | null;
  selectedConversationId: string | null;
  setSelectedConversationId: (value: string | null) => void;
  tooltipPosition: { top: number; left: number } | null;
  setTooltipPosition: (pos: { top: number; left: number } | null) => void;
  handleConversationClick: (id: string, title: string, event: React.MouseEvent) => void;
}

export function ChatMainColumn({
  isFullscreen,
  ...props
}: ChatMainColumnProps) {
  const {
    onClose,
    onToggleFullscreen,
    activeSidebar,
    setActiveSidebar,
    currentConversation,
    startNewConversation,
    isMobile,
    messages,
    isError,
    handleReplayMessage,
    onFollowUpClick,
    isInitializing,
    input,
    setInput,
    handleSendMessage,
    isLoading,
    currentConversationId,
    handleFileUploaded,
    pendingAttachments,
    autoExtractTasks,
    onToggleAutoExtract,
    selectedSpecialist,
    onSpecialistChange,
    selectedProvider,
    onProviderChange,
    selectedModel,
    onModelChange,
    reasoningEffort,
    onReasoningEffortChange,
    isGeminiOAuthConnected,
    backgroundMode,
    onToggleBackgroundMode,
    selectedNotebookId,
    onSelectNotebook,
    contextUsage,
    pendingQuestion,
    onAnswerQuestion,
    allActionItems,
    handleActionItemsUpdate,
    executeQuickAction,
    conversationList,
    loadConversation,
    deleteConversation,
    selectedContextIds,
    toggleContextSelection,
    summarizeSelectedConversations,
    isSummarizing,
    summarizedContext,
    selectedConversationId,
    setSelectedConversationId,
    tooltipPosition,
    setTooltipPosition,
    handleConversationClick,
  } = props;

  const sidebarNav = (
    <SidebarNavigation
      activeSidebar={activeSidebar}
      setActiveSidebar={setActiveSidebar}
      isMobile={isMobile}
      onWorkflowRun={(id) => executeQuickAction(`Run workflow: ${id}`)}
      allActionItems={allActionItems}
      handleActionItemsUpdate={handleActionItemsUpdate}
      executeQuickAction={executeQuickAction}
      conversationList={conversationList}
      currentConversationId={currentConversationId ?? null}
      startNewConversation={startNewConversation}
      loadConversation={loadConversation}
      deleteConversation={deleteConversation}
      selectedContextIds={selectedContextIds}
      toggleContextSelection={toggleContextSelection}
      summarizeSelectedConversations={summarizeSelectedConversations}
      isSummarizing={isSummarizing}
      summarizedContext={summarizedContext}
      updatedTitles={{}}
      selectedConversationId={selectedConversationId}
      setSelectedConversationId={setSelectedConversationId}
      tooltipPosition={tooltipPosition}
      setTooltipPosition={setTooltipPosition}
      handleConversationClick={handleConversationClick}
    />
  );

  const mainContent = (
    <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">
      <ChatMessageList
        messages={messages}
        isError={isError}
        onReplayMessage={handleReplayMessage}
        onFollowUpClick={onFollowUpClick}
        isInitializing={isInitializing}
      />
      <ChatInputArea
        input={input}
        setInput={setInput}
        handleSendMessage={handleSendMessage}
        isLoading={isLoading}
        currentConversationId={currentConversationId}
        handleFileUploaded={handleFileUploaded}
        pendingAttachments={pendingAttachments}
        onSetActiveSidebar={setActiveSidebar}
        autoExtractTasks={autoExtractTasks}
        onToggleAutoExtract={onToggleAutoExtract}
        selectedSpecialist={selectedSpecialist}
        onSpecialistChange={onSpecialistChange}
        selectedProvider={selectedProvider as "gemini" | "openrouter"}
        onProviderChange={onProviderChange}
        selectedModel={selectedModel}
        onModelChange={onModelChange}
        reasoningEffort={reasoningEffort}
        onReasoningEffortChange={onReasoningEffortChange}
        isGeminiDelegated={isGeminiOAuthConnected}
        backgroundMode={backgroundMode}
        onToggleBackgroundMode={onToggleBackgroundMode}
        selectedNotebookId={selectedNotebookId}
        onSelectNotebook={onSelectNotebook}
        contextUsage={contextUsage}
        pendingQuestion={pendingQuestion}
        onAnswerQuestion={onAnswerQuestion}
      />
    </div>
  );

  const header = (
    <ChatHeader
      isFullscreen={isFullscreen}
      onClose={onClose}
      onToggleFullscreen={onToggleFullscreen}
      activeSidebar={activeSidebar}
      onToggleSidebar={setActiveSidebar}
      currentConversation={currentConversation}
      onStartNewChat={startNewConversation}
    />
  );

  return (
    <>
      {header}
      <div className="flex flex-row flex-1 min-h-0 overflow-hidden relative">
        {sidebarNav}
        {mainContent}
      </div>
    </>
  );
}
